import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EventService } from '../core/services/event.service';
import { ToastService } from '../core/services/toast.service';
import { environment } from 'src/environments/environment';
import { createS3Client, createCognitoCredentials, createUpload } from '../utils/aws-s3.util';

@Component({
    selector: 'app-event-branding',
    templateUrl: './event-branding.component.html',
    styleUrls: ['./event-branding.component.css']
})
export class EventBrandingComponent implements OnInit {
    /** Set by Manage Event when this is rendered inline in its own panel — its
     * detail-header already shows "Event Branding" with a close (X), so this
     * component's own back button and title would just duplicate it. */
    @Input() hideHeader: boolean = false;

    brandingForm!: FormGroup;
    eventId: any;
    eventData: any;
    isEditable: boolean = false;
    isLoading: boolean = false;
    isSaving: boolean = false;
    logoUrl: string | null = null;
    logoFile: File | null = null;
    originalFormValues: any;
    isBrandingActive: boolean = true;
    isHost: any;
    private identityPoolId: string = environment.aws.IdentityPoolId;
    private generatedLogoName: string | null = null;
    private existingLogoName: string | null = null;

    constructor(
        private fb: FormBuilder,
        private location: Location,
        private router: Router,
        private route: ActivatedRoute,
        private eventService: EventService,
        private toastService: ToastService,
        private afAuth: AngularFireAuth
    ) { }

    async ngOnInit() {
        this.eventId = localStorage.getItem('eventId');
        this.isHost = localStorage.getItem('isHost')?.toString();
        this.initForm();
        await this.loadBrandingData();
    }

    initForm() {
        this.brandingForm = this.fb.group({
            brandName: ['', [Validators.required, Validators.maxLength(100)]],
            facebookLink: ['', [Validators.pattern(/^(https?:\/\/)?(www\.)?facebook\.com\/.+/i)]],
            instagramLink: ['', [Validators.pattern(/^(https?:\/\/)?(www\.)?instagram\.com\/.+/i)]],
            websiteLink: ['', [Validators.pattern(/^(https?:\/\/)[\w\-]+(\.[\w\-]+)+[/#?]?.*$/)]],
            phoneNumber: ['', [Validators.pattern(/^[+]?[\d\s()-]{10,15}$/)]],
            isActive: [true]
        });
    }

    async loadBrandingData() {
        if (!localStorage.getItem('e')) {
            this.toastService.showToast('We lost track of which album this is. Go back and open it again.', 0);
            return;
        }

        this.isLoading = true;
        try {
            (await this.eventService.getEventBranding(localStorage.getItem('e'))).subscribe({
                next: (response: any) => {
                    if (response && response.result) {
                        const data = response.result;
                        this.isBrandingActive = data.showEventBranding;
                        this.brandingForm.patchValue({
                            brandName: data.name || '',
                            facebookLink: data.facebook_link || '',
                            instagramLink: data.instagram_link || '',
                            websiteLink: data.website || '',
                            phoneNumber: data.phone_number || '',
                            isActive: this.isBrandingActive
                        });
                        this.logoUrl = data.logoUrl || null;
                        this.existingLogoName = data.logo_name || null;
                        this.originalFormValues = this.brandingForm.value;
                    }
                    this.isLoading = false;
                },
                error: (err: any) => {
                    // console.error('Error loading branding data:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to load branding data', 0);
                    this.isLoading = false;
                }
            });
        } catch (error) {
            // console.error('Error:', error); //__CONSOLE_LOG__
            this.isLoading = false;
        }
    }

    toggleEdit() {
        if (this.isEditable) {
            this.cancelEdit();
        } else {
            this.isEditable = true;
            this.originalFormValues = this.brandingForm.value;
        }
    }

    cancelEdit() {
        this.brandingForm.patchValue(this.originalFormValues);
        this.logoFile = null;
        this.generatedLogoName = null;
        this.isEditable = false;
    }

    onLogoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                this.toastService.showToast('Logo size should not exceed 2MB', 0);
                return;
            }

            // Validate file type
            if (!file.type.startsWith('image/')) {
                this.toastService.showToast('Please select a valid image file', 0);
                return;
            }

            this.logoFile = file;

            // Create preview
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.logoUrl = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    }

    triggerFileInput() {
        const fileInput = document.getElementById('logoInput') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    }

    removeLogo() {
        this.logoUrl = null;
        this.logoFile = null;
        this.generatedLogoName = null;
        const fileInput = document.getElementById('logoInput') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    async uploadLogoToS3(): Promise<string | null> {
        if (!this.logoFile) return this.logoUrl;

        try {
            // Get Firebase token for Cognito authentication
            const currentUser = await this.afAuth.currentUser;
            if (!currentUser) {
                throw new Error("No authenticated user found.");
            }

            const firebaseToken = await currentUser.getIdToken(true);
            if (!firebaseToken) {
                throw new Error('Failed to acquire firebase token for Cognito authentication');
            }

            // Create S3 client with Cognito Identity Pool credentials (fetched lazily)
            const region = 'us-west-1';
            const s3 = await createS3Client({
                region,
                credentials: await createCognitoCredentials({
                    identityPoolId: this.identityPoolId,
                    logins: {
                        'securetoken.google.com/picaggo-235807': firebaseToken
                    },
                    clientConfig: { region }
                })
            });

            // Create unique file name based on eventId
            const fileExtension = this.logoFile.name.substring(this.logoFile.name.lastIndexOf('.'));
            const timestamp = Date.now();
            const eventId = localStorage.getItem('e');
            this.generatedLogoName = `${eventId}_${timestamp}${fileExtension}`;
            const fileName = `event-branding/${eventId}/${this.generatedLogoName}`;
            // console.log('generated file name:', this.generatedLogoName); //__CONSOLE_LOG__
            
            const params = {
                Bucket: environment.aws.bucketName,
                Key: fileName,
                Body: this.logoFile,
                ContentType: this.logoFile.type
            };

            const data = await (await createUpload({ client: s3, params })).done();
            return (data as any).Location;
        } catch (error) {
            // console.error('Error uploading logo:', error); //__CONSOLE_LOG__
            this.toastService.showToast('Failed to upload logo', 0);
            return null;
        }
    }

    async saveBranding() {
        if (this.brandingForm.invalid) {
            this.toastService.showToast('Please fill all required fields correctly', 0);
            Object.keys(this.brandingForm.controls).forEach(key => {
                this.brandingForm.get(key)?.markAsTouched();
            });
            return;
        }

        this.isLoading = true;

        try {
            // Upload logo if changed
            let uploadedLogoUrl = this.logoUrl;
            if (this.logoFile) {
                uploadedLogoUrl = await this.uploadLogoToS3();
                if (!uploadedLogoUrl) {
                    this.isLoading = false;
                    return;
                }
            }

            const brandingData = {
                event_id: localStorage.getItem('e'),
                name: this.brandingForm.value.brandName,
                logo_name: this.logoFile ? this.generatedLogoName : this.existingLogoName,
                facebook_link: this.brandingForm.value.facebookLink,
                instagram_link: this.brandingForm.value.instagramLink,
                website: this.brandingForm.value.websiteLink,
                phone_number: this.brandingForm.value.phoneNumber,
                is_active: this.brandingForm.value.isActive
            };

            (await this.eventService.saveEventBranding(brandingData)).subscribe({
                next: (response: any) => {
                    this.toastService.showToast('Branding settings saved successfully', 1);
                    this.isEditable = false;
                    this.originalFormValues = this.brandingForm.value;
                    // Update existingLogoName if a new logo was uploaded
                    if (this.generatedLogoName) {
                        this.existingLogoName = this.generatedLogoName;
                    }
                    this.logoFile = null;
                    this.generatedLogoName = null;
                    this.isLoading = false;
                },
                error: (err: any) => {
                    // console.error('Error saving branding:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to save branding settings', 0);
                    this.isLoading = false;
                }
            });
        } catch (error) {
            // console.error('Error:', error); //__CONSOLE_LOG__
            this.toastService.showToast('An error occurred while saving', 0);
            this.isLoading = false;
        }
    }

    async onToggleChange() {
        const isActive = this.brandingForm.get('isActive')?.value;
        const eventId = localStorage.getItem('e');
        if (!eventId && !localStorage.getItem('e')) {
            this.toastService.showToast('We lost track of which album this is. Go back and open it again.', 0);
            return;
        }

        try {
            (await this.eventService.updateEventBrandingStatus(eventId, isActive)).subscribe({
                next: (response: any) => {
                    this.toastService.showToast(`Branding ${isActive ? 'enabled' : 'disabled'} successfully`, 1);
                    this.isBrandingActive = isActive;
                },
                error: (err: any) => {
                    // console.error('Error updating branding status:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to update branding status', 0);
                    // Revert toggle on error
                    this.brandingForm.patchValue({ isActive: !isActive });
                }
            });
        } catch (error) {
            // console.error('Error:', error); //__CONSOLE_LOG__
            this.toastService.showToast('An error occurred while updating', 0);
            // Revert toggle on error
            this.brandingForm.patchValue({ isActive: !isActive });
        }
    }

    goBack() {
        if (localStorage.getItem('manageEventReturn') === 'true') {
            localStorage.removeItem('manageEventReturn');
            this.router.navigateByUrl('/manage-event');
        } else {
            this.location.back();
        }
    }

    get formControls() {
        return this.brandingForm.controls;
    }
}
